import React, { useEffect, useRef, useState } from "react";
import {
  duplicateSignPdfPlacement,
  getActiveResolvedSignPdfMark,
  getActiveSignPdfPlacement,
  applySignPdfBlockPositionPatch,
  patchActiveSignPdfPlacement,
  removeSignPdfPlacementOrBlock,
  SIGN_PDF_FIELD_KINDS,
  SIGN_PDF_FIELD_OPTIONS,
  ungroupSignPdfBlock,
} from "./signPdfPlacements";
import {
  resolveSignPdfFontSizePx,
  SIGN_PDF_MODES,
  signPdfFontSizeFieldsFromPx,
  signPdfModeFromSettings,
} from "./signPdfSettings";
import { CuteDropdown } from "./SignPdfCuteDropdown";
import { SignPdfPositionDropdown } from "./SignPdfPositionDropdown";
import {
  SignPdfDeleteIcon,
  SignPdfDuplicateIcon,
  SignPdfUngroupIcon,
} from "./SignPdfToolbarIcons";
import "./SignPdfSignatureToolbar.css";
import "./SignPdfFieldToolbar.css";

const SIG_COLORS = [
  "#000000",
  "#111111",
  "#333333",
  "#666666",
  "#9E9E9E",
  "#E0E0E0",
  "#F5F5F5",
  "#FFFFFF",
  "#B71C1C",
  "#E53935",
  "#FF6F00",
  "#F9A825",
  "#2E7D32",
  "#00897B",
  "#1565C0",
  "#0D47A1",
];

const SIG_FONT_SIZE_PRESETS = [
  12, 14, 16, 18, 20, 24, 28, 30, 36, 42, 48, 56, 64, 72,
];

function readOpacityPct(mark) {
  const n = Number(mark?.opacity);
  if (!Number.isFinite(n)) return 100;
  if (n > 0 && n <= 1) return Math.round(n * 100);
  return Math.max(0, Math.min(100, Math.round(n)));
}

/**
 * Signature / Initials toolbar — same chrome + Color/Opacity UX as Field toolbar.
 * Font size is shown only for typed (font) marks — not upload/draw images.
 */
const SignPdfSignatureToolbar = ({
  session,
  onSessionChange,
  currentPage = 1,
  visible = false,
  disabled = false,
  previewStageSize = { width: 420, height: 420 },
}) => {
  const [openId, setOpenId] = useState(null);
  const rootRef = useRef(null);

  const active = getActiveSignPdfPlacement(session);
  const isSigKind =
    active?.kind === SIGN_PDF_FIELD_KINDS.SIGNATURE ||
    active?.kind === SIGN_PDF_FIELD_KINDS.INITIALS;

  useEffect(() => {
    if (!openId) return undefined;
    const onDown = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (e.target instanceof Element && e.target.closest(".sp-dd__menu")) {
        return;
      }
      setOpenId(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [openId]);

  useEffect(() => {
    setOpenId(null);
  }, [visible, active?.id]);

  if (!visible || !active || !isSigKind) return null;

  const mark = getActiveResolvedSignPdfMark(session, currentPage);
  if (!mark) return null;

  const kindLabel =
    SIGN_PDF_FIELD_OPTIONS.find((f) => f.id === active.kind)?.label ||
    "Signature";
  const isLogoMark = Boolean(mark.logoUrl);
  const isTypedFont =
    !isLogoMark && signPdfModeFromSettings(mark) === SIGN_PDF_MODES.TYPE;
  const color = String(
    (isLogoMark
      ? mark.logoTintColor || mark.color
      : mark.color || mark.logoTintColor) || "#111111"
  ).toUpperCase();
  const opacityPct = readOpacityPct(mark);
  const opacitySliderBg = `linear-gradient(to right, #4f46e5 0%, #4f46e5 ${opacityPct}%, #2a2a2a ${opacityPct}%, #2a2a2a 100%)`;
  const stageW = previewStageSize?.width || 420;
  const stageH = previewStageSize?.height || 420;
  const fontSizePx = Math.round(
    resolveSignPdfFontSizePx({ ...mark, ...active }, stageW, stageH)
  );

  const patch = (partial) => {
    if (disabled) return;
    onSessionChange?.((prev) => {
      const a = getActiveSignPdfPlacement(prev);
      const isBlockGeo =
        a?.blockId &&
        partial &&
        ("position" in partial ||
          "offsetX" in partial ||
          "offsetY" in partial ||
          "margin" in partial);
      if (isBlockGeo) {
        let next = applySignPdfBlockPositionPatch(
          prev,
          a.blockId,
          a.id,
          partial
        );
        const solo = { ...partial };
        delete solo.position;
        delete solo.offsetX;
        delete solo.offsetY;
        delete solo.margin;
        if (Object.keys(solo).length) {
          next = patchActiveSignPdfPlacement(next, solo, currentPage);
        }
        return next;
      }
      return patchActiveSignPdfPlacement(prev, partial, currentPage);
    });
  };

  const setColor = (hex) => {
    patch({
      color: hex,
      logoTintColor: hex,
      signPdfCanvasTheme: null,
    });
  };

  const patchFontSize = (nextPx) => {
    const px = Math.max(8, Math.min(120, Math.round(Number(nextPx) || 30)));
    patch(
      signPdfFontSizeFieldsFromPx(px, stageW, stageH, {
        logo: isLogoMark,
      })
    );
  };

  return (
    <div
      className="sp-sig-toolbar sp-field-toolbar"
      ref={rootRef}
      aria-live="polite"
    >
      <div
        className={`sp-sig-toolbar__bar sp-field-toolbar__bar${
          disabled ? " is-disabled" : ""
        }`}
        role="toolbar"
        aria-label={`${kindLabel} tools`}
      >
        <div className="sp-sig-toolbar__scroll">
          <span className="sp-field-toolbar__kind">{kindLabel}</span>

          <CuteDropdown
            id="color"
            openId={openId}
            setOpenId={setOpenId}
            label="Color"
            disabled={disabled}
            menuClassName="sp-dd__menu--swatches"
            display={
              <span className="sp-dd__swatch-preview">
                <span
                  className="sp-dd__swatch-dot"
                  style={{ backgroundColor: color }}
                />
                Color
              </span>
            }
          >
            <div className="sp-dd__swatches">
              {SIG_COLORS.map((c) => {
                const selected = color === String(c).toUpperCase();
                return (
                  <button
                    key={c}
                    type="button"
                    className={`sp-dd__swatch${selected ? " is-selected" : ""}`}
                    title={c}
                    aria-label={`Color ${c}`}
                    disabled={disabled}
                    onClick={() => {
                      setColor(c);
                      setOpenId(null);
                    }}
                  >
                    <span
                      className="sp-dd__swatch-chip"
                      style={{ backgroundColor: c }}
                      aria-hidden="true"
                    />
                    <span>{c}</span>
                  </button>
                );
              })}
            </div>
          </CuteDropdown>

          {isTypedFont ? (
            <CuteDropdown
              id="size"
              openId={openId}
              setOpenId={setOpenId}
              label="Size"
              disabled={disabled}
              menuClassName="sp-dd__menu--compact"
              display={`${fontSizePx}px`}
            >
              {SIG_FONT_SIZE_PRESETS.map((px) => (
                <button
                  key={px}
                  type="button"
                  role="option"
                  aria-selected={fontSizePx === px}
                  className={`sp-dd__option${
                    fontSizePx === px ? " is-selected" : ""
                  }`}
                  disabled={disabled}
                  onClick={() => {
                    patchFontSize(px);
                    setOpenId(null);
                  }}
                >
                  {px}px
                </button>
              ))}
            </CuteDropdown>
          ) : null}

          <SignPdfPositionDropdown
            mark={mark}
            openId={openId}
            setOpenId={setOpenId}
            disabled={disabled}
            onPatch={patch}
          />

          <CuteDropdown
            id="opacity"
            openId={openId}
            setOpenId={setOpenId}
            label="Opacity"
            disabled={disabled}
            className="sp-dd--opacity"
            menuClassName="sp-dd__menu--opacity-slider"
            display={`Opacity ${opacityPct}%`}
          >
            <div
              className="sp-field-opacity"
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <div className="sp-field-opacity__label">
                <span>Opacity</span>
                <span>{opacityPct}%</span>
              </div>
              <input
                type="range"
                className="sp-sig-opacity-slider"
                min={0}
                max={100}
                step={1}
                value={opacityPct}
                disabled={disabled}
                style={{ "--sp-sig-opacity-slider-bg": opacitySliderBg }}
                aria-label="Opacity"
                onChange={(e) => {
                  const n = Math.max(
                    0,
                    Math.min(100, Number(e.target.value) || 0)
                  );
                  patch({ opacity: n });
                }}
              />
            </div>
          </CuteDropdown>

          <span className="sp-sig-divider" aria-hidden="true" />

          {active.blockId ? (
            <button
              type="button"
              className="sp-dd__icon"
              disabled={disabled}
              title="Ungroup signing block"
              aria-label="Ungroup signing block"
              onClick={() => {
                if (disabled) return;
                setOpenId(null);
                onSessionChange?.((prev) =>
                  ungroupSignPdfBlock(prev, active.blockId)
                );
              }}
            >
              <SignPdfUngroupIcon />
            </button>
          ) : null}

          <button
            type="button"
            className="sp-dd__icon"
            disabled={disabled}
            title="Duplicate"
            aria-label="Duplicate"
            onClick={() => {
              if (disabled) return;
              setOpenId(null);
              onSessionChange?.((prev) =>
                duplicateSignPdfPlacement(prev, active.id, currentPage)
              );
            }}
          >
            <SignPdfDuplicateIcon />
          </button>

          <button
            type="button"
            className="sp-dd__icon"
            disabled={disabled}
            title={active.blockId ? "Delete signing block" : "Delete"}
            aria-label={active.blockId ? "Delete signing block" : "Delete"}
            onClick={() => {
              if (disabled) return;
              setOpenId(null);
              onSessionChange?.((prev) =>
                removeSignPdfPlacementOrBlock(prev, active.id)
              );
            }}
          >
            <SignPdfDeleteIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignPdfSignatureToolbar;
